import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CreateOECodingVariableCommand, OECodingClient, IOECodingClient, SorterDescriptor, SortDirections } from "app/ApiClient";
import { LoadingState } from "types";
import { initialOECodingState, 
  PageContextOfOECodingFilter,
  OECoding,
  OECodingMessage,
  OECodingFilter} from "./oeCodingState";
import { RootState } from "@store";
import { oeCodingTableConfig } from "const/OECoding";

const oeCodingClient: IOECodingClient = new OECodingClient(process.env.REACT_APP_API_URL);

export const createOECodingVariable = createAsyncThunk(
  "oeCoding/createOECodingVariable",
  async (data: CreateOECodingVariableCommand, { dispatch }) => {
    const result = await oeCodingClient.createOECodingVariable({ ...data });
    if (result)
      dispatch(getAllOECodingsForProject({ projectId: data.projectId ?? 0, encodedVersion: data.encodedVersionId ?? "" }))
    return result;
  },
);

const sortOECoding = (oeCodingList: OECoding[], sortList?: SorterDescriptor[]): OECoding[] => {
  if (sortList && sortList.length > 0) {
    let oeCodings: OECoding[] = oeCodingList;
    for (const sortDesc of sortList) {
      const sortKey = sortDesc.field as keyof OECoding;
      const sortedIntegrations = oeCodings.sort((a, b) => {
        const dataA = a[sortKey]?.toString();
        const dataB = b[sortKey]?.toString();

        const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

        if (dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? dataA.localeCompare(dataB) : dataB.localeCompare(dataA);
        }
        if (dataA && !dataB) {
          return sortDirection === SortDirections.Asceding ? 1 : -1;
        }
        if (!dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? -1 : 1;
        }
        return 0;
      });
      oeCodings = sortedIntegrations;
    }
    return oeCodings;
  }
  return oeCodingList;
};

export const getAllOECodingsForProject = createAsyncThunk(
  "oeCoding/getAllOECodingsForProject",
  async ({ projectId, encodedVersion, forceFetch }: { projectId: number, encodedVersion: string, forceFetch?: boolean }, { getState }) => {
    const { oeCoding} = getState() as RootState;
    const { page: pageContext } = oeCoding.pageContext;

    let allData: OECoding[] = oeCoding.oeCodingVariables.allData;
    if (!allData?.length || forceFetch) {
      const allOECodings = await oeCodingClient.getOECodedVariablesForProject(projectId, encodedVersion);     
      allData = allOECodings;
    }

    const {
      pageNumber = oeCodingTableConfig.DefaultPageNumber,
      pageSize = oeCodingTableConfig.DefaultPageSize,
    } = pageContext ?? {};

    const oeCodingsFilters = (oeCoding.filters) ?? {};

    let filteredData: OECoding[] = [...allData];
   
    if (Object.keys(oeCodingsFilters).length > 0) {
      filteredData = filteredData.filter((item: OECoding) =>
        Object.keys(oeCodingsFilters).every((key) => {
          const filterValues = oeCodingsFilters[key as keyof OECodingFilter];
          if (Array.isArray(filterValues) && filterValues.length > 0) {
            const fieldValue = item[key as keyof OECoding];
            const actualFieldValue = `${fieldValue == undefined ? "" : fieldValue}`;
            return filterValues.includes(actualFieldValue);
          }
          return true;
        }),
      );
    }
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    const sortedIntegrations = sortOECoding(filteredData, oeCoding.pageContext.sortList);

    let result = oeCoding.oeCodingVariables;
    result = {
      allData: allData ?? [],
      pageData: [],
      pageNumber: oeCoding.pageContext.page?.pageNumber,
      pageSize: oeCoding.pageContext.page?.pageSize,
    };

    result = {
      ...result,
      totalCount: sortedIntegrations.length,
      pageData: sortedIntegrations.slice(startIndex, endIndex),
    };
    return result;
  },
);

const oeCodingSlice = createSlice({
  name: "oeCoding",
  initialState: initialOECodingState,
  reducers: {
    resetOECodingVariableState: (state) => {
      state.createOECodingVariableState = LoadingState.idle;
    },
    removeAlertMessage :(state, { payload}: {payload: string})=>{
      const filteredMessages = state.alertMessages.filter(x=>x.id !== payload);
      state.alertMessages = [...filteredMessages];
    },
    pushOECodingAlertMessage :(state, { payload}: PayloadAction<OECodingMessage>)=>{
      state.alertMessages = [...state.alertMessages, {...payload}];
    },
    setOECodingPageContext: (
      state,
      { payload }: { payload: { pageContext: PageContextOfOECodingFilter; isPageChanged?: boolean } },
    ) => {      
      const isPageChanged = payload.isPageChanged;
      const pageContext = payload.pageContext;
      state.pageContext = isPageChanged ? { ...state.pageContext, page: pageContext.page } : pageContext;  
    },

    setOECodingFilters: (state, { payload }: { payload: { [key in keyof OECodingFilter]: string[] } }) => {
      state.filters = { ...state.filters, ...payload };      
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createOECodingVariable.pending, (state) => {
      state.createOECodingVariableState = LoadingState.pending;
    });
    builder.addCase(createOECodingVariable.fulfilled, (state) => {
      state.createOECodingVariableState = LoadingState.succeed;
    });
    builder.addCase(createOECodingVariable.rejected, (state) => {
      state.createOECodingVariableState = LoadingState.failed;
    });

    builder.addCase(getAllOECodingsForProject.pending, (state) => {
      state.oeCodingLoadingState = LoadingState.pending;
    });
    builder.addCase(getAllOECodingsForProject.fulfilled, (state, { payload }) => {
      state.oeCodingLoadingState = LoadingState.succeed;
      state.oeCodingVariables = payload ?? []
    });
    builder.addCase(getAllOECodingsForProject.rejected, (state) => {
      state.oeCodingLoadingState = LoadingState.failed;      
    });

  },
});

export const { resetOECodingVariableState, removeAlertMessage, pushOECodingAlertMessage, setOECodingPageContext, setOECodingFilters } = oeCodingSlice.actions;
export default oeCodingSlice.reducer;
